import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type QuickSelectKey =
  | 'smallDiameter'
  | 'bigDiameter'
  | 'noLength'
  | 'noGrade'
  | 'noQuantity'
  | 'noMatch'
  | 'weldable'
  | 'notWeldable';

export const QUICK_SELECT_KEYS: QuickSelectKey[] = [
  'smallDiameter',
  'bigDiameter',
  'noLength',
  'noGrade',
  'noQuantity',
  'noMatch',
  'weldable',
  'notWeldable',
];

export const QUICK_SELECT_LABELS: Record<QuickSelectKey, string> = {
  // 14ykpqtr22u — 16mm is a big size; the chips split at BIG_DIAMETER_MIN_MM (pricing.ts).
  // Keys stay as-is: they're persisted in localStorage (visibility prefs).
  smallDiameter: 'Small Ø (<16 mm)',
  bigDiameter: 'Big Ø (≥16 mm)',
  noLength: 'No length',
  noGrade: 'No grade',
  noQuantity: 'No qty',
  noMatch: 'No match',
  weldable: 'Weldable',
  notWeldable: 'Not weldable',
};

const DEFAULT_VISIBILITY: Record<QuickSelectKey, boolean> = {
  smallDiameter: true,
  bigDiameter: true,
  noLength: true,
  noGrade: true,
  noQuantity: true,
  noMatch: true,
  weldable: true,
  notWeldable: true,
};

interface QuotationPreferencesState {
  visibleQuickSelects: Record<QuickSelectKey, boolean>;
  setQuickSelectVisible: (key: QuickSelectKey, visible: boolean) => void;
}

export const useQuotationPreferencesStore = create<QuotationPreferencesState>()(
  persist(
    (set) => ({
      visibleQuickSelects: { ...DEFAULT_VISIBILITY },
      setQuickSelectVisible: (key, visible) =>
        set((state) => ({
          visibleQuickSelects: { ...state.visibleQuickSelects, [key]: visible },
        })),
    }),
    {
      name: 'csc-wms-quotation-prefs',
      storage: createJSONStorage(() => localStorage),
      // Merge persisted shape over defaults so adding a new quick-select key
      // later doesn't leave existing users with `undefined` for it.
      merge: (persisted, current) => {
        const persistedState = (persisted as Partial<QuotationPreferencesState>) ?? {};
        return {
          ...current,
          visibleQuickSelects: {
            ...DEFAULT_VISIBILITY,
            ...(persistedState.visibleQuickSelects ?? {}),
          },
        };
      },
    },
  ),
);
